import React, { useState, useCallback, useEffect } from 'react';

import SingleVerificationInput from './SingleVerificationInput';

interface VerificationCodeInputProps {
  verificationCodeValues: string[];
  onChangeVerificationCodeValues: (values: string[]) => void;
  autoFocus?: boolean;
  isNumberInput?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
}

const getCorrectedValue = (value: string, isNumberInput?: boolean) => {
  if (!isNumberInput) {
    return value;
  }

  return !value || /\d/.test(value) ? value : '';
};

const VerificationCodeInput = ({
  isNumberInput,
  autoFocus,
  disabled,
  verificationCodeValues,
  onChangeVerificationCodeValues,
  inputClassName,
}: VerificationCodeInputProps) => {
  const [activeInput, setActiveInput] = useState(0);

  useEffect(() => {
    const isEmptyCode = verificationCodeValues.every((value) => !value);

    if (!isEmptyCode) {
      return;
    }

    setActiveInput(0);
  }, [verificationCodeValues, setActiveInput]);

  const changeInputCodeByIndex = useCallback(
    (value: string, index: number) => {
      const newCodeValues = [...verificationCodeValues];

      newCodeValues[index] = value[0] || '';
      onChangeVerificationCodeValues(newCodeValues);
    },
    [verificationCodeValues, onChangeVerificationCodeValues],
  );

  const focusInput = useCallback(
    (inputIndex: number) => {
      setActiveInput(Math.max(Math.min(verificationCodeValues.length - 1, inputIndex), 0));
    },
    [verificationCodeValues.length],
  );

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = getCorrectedValue(event.currentTarget.value, isNumberInput);

      if (!value) {
        return;
      }

      changeInputCodeByIndex(value, activeInput);
      focusInput(activeInput + 1);
    },
    [changeInputCodeByIndex, focusInput, isNumberInput, activeInput],
  );

  const onBlur = useCallback(() => setActiveInput(-1), []);

  const handleOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'Backspace': {
          event.preventDefault();

          if (verificationCodeValues[activeInput]) {
            return changeInputCodeByIndex('', activeInput);
          }

          changeInputCodeByIndex('', activeInput - 1);

          return focusInput(activeInput - 1);
        }
        case 'Delete': {
          event.preventDefault();

          return changeInputCodeByIndex('', activeInput);
        }
        case 'ArrowLeft': {
          event.preventDefault();

          return focusInput(activeInput - 1);
        }
        case 'ArrowRight': {
          event.preventDefault();

          return focusInput(activeInput + 1);
        }
        default: {
          return null;
        }
      }
    },
    [activeInput, changeInputCodeByIndex, verificationCodeValues, focusInput],
  );

  const handleFocus = useCallback(
    (index) => (event: React.FocusEvent<HTMLInputElement>) => {
      event.preventDefault();

      focusInput(index);
    },
    [focusInput],
  );

  const handleOnPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();

      const pastedData = event.clipboardData
        .getData('text/plain')
        .trim()
        .slice(0, verificationCodeValues.length - activeInput)
        .split('');

      let nextFocusIndex = 0;
      const newCodeValues = [...verificationCodeValues];

      newCodeValues.forEach((value, index) => {
        if (index < activeInput) {
          return;
        }

        const changedValue = getCorrectedValue(pastedData.shift() || value, isNumberInput);

        if (changedValue) {
          newCodeValues[index] = changedValue;
          nextFocusIndex = index;
        }
      });

      onChangeVerificationCodeValues(newCodeValues);
      setActiveInput(Math.min(nextFocusIndex + 1, verificationCodeValues.length - 1));
    },
    [activeInput, verificationCodeValues, onChangeVerificationCodeValues, isNumberInput],
  );

  return (
    <>
      {verificationCodeValues.map((value, index) => (
        <SingleVerificationInput
          key={`single-input-${index}`}
          focus={activeInput === index}
          autoFocus={autoFocus}
          value={value}
          onFocus={handleFocus(index)}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          onBlur={onBlur}
          onPaste={handleOnPaste}
          className={inputClassName}
          disabled={disabled}
          isNumberInput={isNumberInput}
        />
      ))}
    </>
  );
};

export default VerificationCodeInput;
